import { motion } from "framer-motion";
import { Map, Mountain, Waves } from "lucide-react";
import { IMAGES } from "./data";
import { Reveal, Overline } from "./primitives";

const PILLARS = [
  { icon: Map, label: "Digital Mapping" },
  { icon: Mountain, label: "Elevation Data" },
  { icon: Waves, label: "Hydraulic Engineering" },
];

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-white py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal>
              <Overline>About PipeMitra</Overline>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-heading text-4xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl">
                Engineering Meets
                <span className="text-[#5aa72f]"> Agriculture</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-7 text-lg leading-relaxed text-slate-600">
                PipeMitra combines digital mapping, elevation data and hydraulic
                engineering calculations to make agricultural pipeline planning
                simpler, scientific and accessible.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-4 text-lg leading-relaxed text-slate-600">
                It is designed for farmers, pipe dealers and pipeline technicians
                across India who need pipeline decisions grounded in engineering
                rather than guesswork.
              </p>
            </Reveal>

            <div className="mt-10 grid grid-cols-3 gap-3">
              {PILLARS.map((p, i) => (
                <Reveal key={p.label} delay={0.2 + i * 0.08}>
                  <div className="group rounded-2xl border border-slate-200 bg-slate-50 p-4 transition-all duration-300 hover:-translate-y-1 hover:border-[#1a6fc4]/30 hover:bg-white hover:shadow-md">
                    <p.icon className="h-6 w-6 text-[#1a6fc4]" strokeWidth={1.6} />
                    <p className="mt-3 text-sm font-semibold leading-snug text-slate-800">
                      {p.label}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.1} className="relative">
            <div className="relative overflow-hidden rounded-3xl border border-slate-200 shadow-sm">
              <img
                src={IMAGES.pipeInField}
                alt="Irrigation pipeline running through a farm field"
                className="aspect-[4/5] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0e3f70]/45 via-transparent to-transparent" />
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/20 bg-white/85 p-5 backdrop-blur-md"
              >
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#1a6fc4]">
                  From field to formula
                </p>
                <p className="mt-2 text-sm font-medium leading-snug text-slate-800">
                  A structured, technology-assisted workflow for planning
                  agricultural pipelines.
                </p>
              </motion.div>
            </div>
            <div className="absolute -right-4 -top-4 -z-10 h-28 w-28 rounded-3xl bg-[#5aa72f]/15" />
            <div className="absolute -bottom-5 -left-5 -z-10 h-24 w-24 rounded-3xl bg-[#1a6fc4]/15" />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
